/* eslint-disable spaced-comment */
/* eslint-disable max-lines */
import CheckoutQuery from 'Query/Checkout.query';
import ProductListQuery from 'Query/ProductList.query';
import {
    CartQuery as SourceCartQuery
} from 'SourceQuery/Cart.query';
import { Field } from 'Util/Query';

/** @namespace PeggysagePwa/Query/Cart/Query */
export class CartQuery extends SourceCartQuery {
    //#region MUTATIONS
    /**
     * Override: add cart fields for popin cart
     * @param cartId
     * @param cartItems
     * @returns {*}
     */
    getAddProductToCartMutation(cartId, cartItems) {
        return new Field('addProductsToCart')
            .addArgument('cartId', 'String!', cartId)
            .addArgument('cartItems', '[CartItemInput!]!', cartItems)
            .addFieldList(this._getAddProductsToCartFields());
    }
    //#endregion

    /**
     * Override: add display_tax_in_total
     * @returns {string[]}
     * @private
     */
    _getCartDisplayConfigFields() {
        return [
            ...super._getCartDisplayConfigFields(),
            'display_tax_in_total'
        ];
    }

    getAddFreeProductToCartMutation(cartId, cartItems, ruleId) {
        return new Field('addFreeProductsToCart')
            .addArgument('cartId', 'String!', cartId)
            .addArgument('cartItems', '[CartItemInput!]!', cartItems)
            .addArgument('ruleId', 'Int!', ruleId)
            .addFieldList(this._getAddProductsToCartFields());
    }

    getRemoveSingleCouponMutation(quoteId, couponCode) {
        const mutation = new Field('removeSingleCoupon')
            .addArgument('coupon_code', 'String!', couponCode)
            .addArgument('cart_id', 'String!', quoteId)
            .addField(this.getCartQuery(quoteId));

        return mutation;
    }

    getCurrentPromoCodes(quoteId) {
        return new Field('getCurrentPromoCodes')
            .addArgument('guestCartId', 'String!', quoteId)
            .addField(
                new Field('coupons').addFieldList(
                    this._getCartAppliedCouponsFields()
                )
            );
    }

    _getAddProductsToCartFields() {
        return [
            this._getUserErrorsField(),
            new Field('cart').addFieldList([
                ...this._getCartTotalsFields(),
                'cross_product_skus'
            ])
        ];
    }

    /**
     * Override
     * @returns {*[]}
     * @private
     */
    _getCartItemsFields() {
        return [
            ...super._getCartItemsFields(),
            'isFreeproduct',
            'isExtraProduct',
            'freeProductRuleId'
        ];
    }

    /**
     * Override
     * @returns {*[]}
     * @private
     */
    _getCartTotalsFields() {
        return [
            ...super._getCartTotalsFields(),
            'freeshipping_min_amount',
            'freeshipping_remaining_amount',
            this._getCartAppliedCouponsField(),
            this._getCartAvailableCouponsField(),
            this._getCartPricesField(),
            'applied_rule_ids',
            'items_qty',
            'selected_shipping_method',
            'total_quantity',
            new Field('selected_payment_method').addField('code'),
            'shipping_info_link',
            // this._getSampleProductsField(),
            this._getGiftWrapProductsField(),
            'gift_wrap_products_title',
            'gift_wrap_products_subtitle',
            this._getGiftProductsChoicesField(),
            this._getExtraCheckoutAgreementsField(),
            // this._getCartBillingAddressField(),
            // this._getCartShippingAddressesField(),
            // this._getCartRelayInfoField(),
            // this._getCartGiftMessageField(),
            CheckoutQuery._getShippingPhoneInfo(),
            this._getExtraProductsField()
        ];
    }

    _getCartAmountFields() {
        return [
            'value',
            'currency'
        ];
    }

    _getCartObjectLabelAmountFields() {
        return [
            'label',
            new Field('amount').addFieldList(this._getCartAmountFields())
        ];
    }

    _getCartPricesField() {
        return new Field('prices').addFieldList([
            new Field('subtotal_excluding_tax').addFieldList(this._getCartAmountFields()),
            new Field('subtotal_including_tax').addFieldList(this._getCartAmountFields()),
            new Field('subtotal_with_discount_excluding_tax').addFieldList(this._getCartAmountFields()),
            new Field('applied_taxes').addFieldList(this._getCartObjectLabelAmountFields()),
            new Field('discounts').addFieldList(this._getCartObjectLabelAmountFields()),
            new Field('grand_total').addFieldList(this._getCartAmountFields()),
            new Field('estimated_shipping_amount').addFieldList(this._getCartAmountFields())
        ]);
    }

    _getCartAppliedCouponsFields() {
        return [
            'code',
            'description'
        ];
    }

    _getCartAppliedCouponsField() {
        return new Field('applied_coupons').addFieldList(
            this._getCartAppliedCouponsFields()
        );
    }

    _getCartAvailableCouponsField() {
        return new Field('available_coupons').addFieldList(
            this._getCartAppliedCouponsFields()
        );
    }

    _getCartBillingAddressField() {
        return new Field('billing_address')
            .addFieldList(this._getCartAddressFields());
    }

    _getCartAddressFields() {
        return [
            'firstname',
            'lastname',
            'street',
            'postcode',
            'city',
            new Field('country').addField('code')
        ];
    }

    _getCartShippingAddressesField() {
        return new Field('shipping_addresses')
            .addFieldList(this._getCartShippingAddressesFields());
    }

    _getCartUpdateField() {
        return new Field('cart')
            .addFieldList(this._getCartTotalsFields());
    }

    _getCartShippingAddressesFields() {
        return [
            ...this._getCartAddressFields(),
            'telephone',
            new Field('available_shipping_methods').addFieldList(
                this._getCartShippingMethodFields()
            ),
            new Field('selected_shipping_method').addFieldList([
                ...this._getCartShippingMethodFields(),
                new Field('amount').addFieldList(this._getCartAmountFields())
            ])
        ];
    }

    _getCartShippingMethodFields() {
        return [
            'method_code',
            'method_title',
            'carrier_code',
            'carrier_title'
        ];
    }

    _getCartRelayInfoField() {
        return new Field('relay_info').addFieldList([
            'relay_id',
            'relay_type'
        ]);
    }

    _getCartGiftMessageField() {
        return new Field('gift_message').addFieldList([
            'from',
            'to',
            'message'
        ]);
    }

    _getSampleProductsField() {
        return new Field('sample_products').addFieldList(ProductListQuery._getCartProductInterfaceFields());
    }

    _getGiftWrapProductsField() {
        return new Field('gift_wrap_products').addFieldList([
            ...ProductListQuery._getCartProductInterfaceFields(),
            ProductListQuery._getPriceRangeField()
        ]);
    }

    _getExtraProductsField() {
        return new Field('extra_products').addFieldList([
            'title',
            'subtitle',
            'image',
            this._getExtraProductsProducts()
        ]);
    }

    _getExtraProductsProducts() {
        return new Field('products').addFieldList([
            ...ProductListQuery._getCartProductInterfaceFields(),
            ProductListQuery._getPriceRangeField()
        ]);
    }

    _getGiftProductsChoicesField() {
        return new Field('gift_products_choices').addFieldList([
            'rule_id',
            'title',
            'max_products_allowed',
            'current_products_in_cart',
            new Field('products').addFieldList([
                'id',
                'sku',
                'name',
                'url',
                new Field('thumbnail').addField('url'),
                'is_in_cart_free_product',
                'cart_item_id',
                'qty_as_free_product'
            ])
        ]);
    }

    _getExtraCheckoutAgreementsField() {
        return new Field('extra_checkout_agreements').addFieldList([
            'link',
            'display_mode',
            'checkbox_text'
        ]);
    }

    _getEmptyCurrentCart(cartId) {
        return new Field('emptyCurrentCart').addArgument('cart_id', 'String', cartId);
    }

    _getBundleOptionValuesFields() {
        return [
            ...super._getBundleOptionValuesFields(),
            'uid',
            'sku'
        ];
    }
}

export default new CartQuery();
